import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useGetRolesQuery, useGetSubscriptionsQuery, useMoveSubscriptionMutation } from 'api/main';
import { userSelectors } from 'reducers';
import { ORDER_BY, roleCategories, sortDirection } from 'utils/constants';
import { parseFullName } from 'utils/helpers';
import { useSmallScreen } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CustomDialogTitle, CustomSelect, Loader } from 'components';
import { DialogContent, DialogActions, Dialog, MenuItem } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    paper: {
        [theme.breakpoints.down('sm')]: {
            minWidth: 'unset',
        },
    },
    dialogContent: {
        padding: theme.spacing(2, 3),
        color: theme.palette.grey[600],
    },
}));

const SubscriptionMoveDialog = ({ open, handleClose, subscriptionId }) => {
    const classes = useStyles();
    const fullScreen = useSmallScreen();

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const [moveSubscription, { isSuccess, isLoading: loading, reset }] =
        useMoveSubscriptionMutation();

    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);

    const [distributorUser, setDistributorUser] = useState('');
    const [errors, setErrors] = useState('');
    const filters = useMemo(
        () => ({
            orderBy: ORDER_BY.id,
            direction: sortDirection.DESC,
            companyId: [currentSubscription?.distributorCompany?.id],
            role: [],
            pagination: false,
            emailConfirmed: 1,
        }),
        [currentSubscription]
    );

    const adminDistributorRoles = useMemo(
        () =>
            roles.filter(
                r =>
                    r.category?.name === roleCategories.ADMIN_DISTRIBUTORS ||
                    r.category?.name === roleCategories.DISTRIBUTORS
            ),
        [roles]
    );

    const { data: { subscriptions = [] } = { subscriptions: [] }, isFetching: dataLoading } =
        useGetSubscriptionsQuery(
            { ...filters, role: adminDistributorRoles.map(role => role.id) },
            {
                skip: !adminDistributorRoles.length || !open,
            }
        );

    const handleChange = useCallback(({ target: { value } }) => {
        setDistributorUser(value);
        setErrors('');
    }, []);

    useEffect(() => {
        if (!open || !subscriptions.length) return;
        setDistributorUser(prevState => (!prevState ? subscriptions[0].user.id : prevState));
    }, [filters, roles, open, subscriptions]);

    const handleCloseMove = useCallback(() => {
        setErrors('');
        setDistributorUser('');
        reset();
        handleClose();
    }, [handleClose, reset]);

    const handleConfirmMove = useCallback(() => {
        moveSubscription({ id: subscriptionId, distributorId: distributorUser });
    }, [distributorUser, moveSubscription, subscriptionId]);

    useEffect(() => {
        if (isSuccess) {
            handleCloseMove();
        }
    }, [isSuccess, handleCloseMove]);

    const mappedUsers = useMemo(
        () =>
            subscriptions.map(({ user }) => (
                <MenuItem key={user.id} value={user.id}>
                    {parseFullName(user)}
                </MenuItem>
            )),
        [subscriptions]
    );

    return (
        <Dialog fullScreen={fullScreen} open={open} classes={{ paper: classes.paper }}>
            <CustomDialogTitle title={texts.TRANSFER_SUBSCRIPTION} handleClose={handleCloseMove} />
            <DialogContent className={classes.dialogContent}>
                <Loader loading={dataLoading || rolesLoader}>
                    <CustomSelect value={distributorUser} onChange={handleChange} error={errors}>
                        {mappedUsers}
                    </CustomSelect>
                </Loader>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={loading} onClick={handleCloseMove}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton loading={loading} disabled={loading} onClick={handleConfirmMove}>
                    {texts.TRANSFER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default SubscriptionMoveDialog;
